class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.queueQuantity(this.dataset.index, 0, event, true);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

// Noom T05 (13.09.2026): the Elixir layout loads Shopify's Standard Events module only behind settings.hm_cart_global; when the library
// is missing (module blocked, or a layout without it) the cart keeps working and only the standard "view" event of cart-items is skipped.
const CartItemsBase = window.StandardEvents && window.StandardEvents.createViewEventElement
  ? window.StandardEvents.createViewEventElement(HTMLElement)
  : HTMLElement;

class CartItems extends CartItemsBase {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    this.addEventListener('change', (event) => {
      if (!event.target.matches?.('input.quantity__input[data-index]')) return;
      this.queueQuantity(event.target.dataset.index, event.target.value, event, false);
    });
  }

  cartUpdateUnsubscriber = undefined;

  static pendingCartDataPromise = null;
  static pendingChanges = new Map();
  static activeUpdate = null;
  static draining = null;
  static quantityTimer = null;

  static inputIdentity(input) {
    try {
      const properties = JSON.parse(input.dataset.hmLineProperties || '{}') || {};
      if (Array.isArray(properties) && properties.length) return null;
      return JSON.stringify([String(input.dataset.quantityVariantId),
        Object.keys(properties).filter(key => properties[key] != null && properties[key] !== '').sort()
          .map(key => [key, String(properties[key])]), String(input.dataset.hmSellingPlan || '')]);
    } catch (_) { return null; }
  }

  queueQuantity(line, quantity, event, immediate) {
    const input = this.querySelector(`#Quantity-${line}`) || this.querySelector(`#Drawer-quantity-${line}`);
    if (!input?.isConnected) return;
    const identity = CartItems.inputIdentity(input);
    const key = input.dataset.quantityLineKey;
    if (!key) {
      input.value = input.getAttribute('value');
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer.setNeedsSync?.(true);
        drawer.showError?.('change');
      } else this.updateLiveRegions(line, window.cartStrings.error);
      return;
    }
    CartItems.pendingChanges.set(key, { key, identity, quantity, tag: this.tagName,
      remove: event.currentTarget instanceof CartRemoveButton, name: document.activeElement?.getAttribute('name') });
    clearTimeout(CartItems.quantityTimer);
    // Keep rapid taps on each line together, without Dawn's 300 ms pause.
    if (immediate) CartItems.flushPendingChanges();
    else if (!CartItems.draining) CartItems.quantityTimer = setTimeout(() => CartItems.flushPendingChanges(), 80);
  }

  static flushPendingChanges() {
    clearTimeout(CartItems.quantityTimer);
    if (CartItems.draining) return CartItems.draining;
    if (!CartItems.pendingChanges.size) return CartItems.activeUpdate;
    CartItems.draining = Promise.resolve().then(async () => {
      if (CartItems.activeUpdate && !await CartItems.activeUpdate) return false;
      while (CartItems.pendingChanges.size) {
        const [key, intent] = CartItems.pendingChanges.entries().next().value;
        CartItems.pendingChanges.delete(key);
        const owner = document.querySelector(intent.tag.toLowerCase());
        const inputs = [...(owner?.querySelectorAll('input.quantity__input[data-index]') || [])];
        let input = inputs.find(input => input.dataset.quantityLineKey === intent.key);
        if (!input && intent.identity) {
          const matches = inputs.filter(input => CartItems.inputIdentity(input) === intent.identity);
          if (matches.length === 1) input = matches[0];
        }
        if (!input) {
          document.querySelector('cart-drawer')?.showError?.('linegone');
          return false;
        }
        const line = input.dataset.index;
        const event = { target: input, currentTarget: owner };
        let result;
        if (intent.remove) result = owner.updateQuantity(line, 0, event, intent.name, input.dataset.quantityVariantId);
        else {
          input.value = String(intent.quantity);
          result = owner.onChange(event);
        }
        if (await result !== true) return false;
      }
      return true;
    }).catch(() => false).then(confirmed => {
      if (!confirmed) {
        CartItems.pendingChanges.clear();
        window.HMLpBonusUI?.cancelCheckout();
      }
      return confirmed;
    }).finally(() => {
      CartItems.draining = null;
      window.HMLpBonusUI?.render();
    });
    return CartItems.draining;
  }

  connectedCallback() {
    // The factory base class auto-dispatches cart:view from the
    // `view-event-payload` attribute (Liquid filter output). The drawer
    // sets `view-event-trigger="manual"` to skip auto-dispatch.
    // Noom R2 (14.09): with the HTMLElement fallback there is no parent connectedCallback — guard it, so the cartUpdate
    // subscription below is always registered (review P2: "connectedCallback is not a function")
    if (typeof super.connectedCallback === 'function') super.connectedCallback();

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') return;
      if (event.drawerRenderedBySource || event.sectionsRenderedBySource) return;
      return this.onCartUpdate();
    });
  }

  // Fetches the full cart shape (used to resolve the cart:lines-update event
  // promise after /cart/add.js, which only returns the added line — not the
  // post-mutation cart aggregates). De-duplicated across concurrent callers.
  static fetchCartData() {
    if (!CartItems.pendingCartDataPromise) {
      const pendingCartDataPromise = fetch(`${routes.cart_url}.json`)
        .then((response) => response.json())
        .catch(() => null)
        .finally(() => {
          if (CartItems.pendingCartDataPromise === pendingCartDataPromise) CartItems.pendingCartDataPromise = null;
        });

      CartItems.pendingCartDataPromise = pendingCartDataPromise;
    }
    return CartItems.pendingCartDataPromise;
  }

  disconnectedCallback() {
    if (typeof super.disconnectedCallback === 'function') super.disconnectedCallback();
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      return this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    // Other controls inside cart-drawer-items (including app UI) also emit change.
    if (!(event.target instanceof HTMLInputElement) ||
        !event.target.isConnected || !this.contains(event.target) ||
        !event.target.matches('input.quantity__input[data-index]')) return;
    return this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=hm-cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          window.HMLpBonusUI?.render();
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
          window.HMLpBonusUI?.render();
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'hm-cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'hm-cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
    // Noom R1 (14.09): the cart page and the drawer live on the same page (global cart) — a confirmed change on the page
    // also re-renders the drawer from the SAME /cart/change response (one write, no second read, no event loop);
    // review P1: the drawer reopened with the old lines after a change on /cart
    if (document.getElementById('CartDrawer')) {
      sections.push({ id: 'CartDrawer', section: 'hm-cart-drawer', selector: '.drawer__inner' });
    }
    return sections;
  }

  updateQuantity(line, quantity, event, name, variantId) {
    if (CartItems.activeUpdate) return false;
    // Noom (T02 v4, review R3): while the cart is uncertain (an add or change the store did not confirm) a change is applied
    // only after a read of the real cart, to the same line by its key — never by its old position; during a read
    // nothing is sent at all. Codex C1 (14.09): the cart PAGE obeys the same drawer state — with the global drawer on the
    // page, cart-items must not write while the drawer reads or is uncertain; the drawer's read then re-renders the page
    // sections too (main-cart-items / main-cart-footer) and applies the click once by line key. Without a drawer on the
    // page (layout without it) the plain path below stays as it was.
    const drawerEl = document.querySelector('cart-drawer');
    if (drawerEl?.crossTabDirty) {
      const pendingInput = this.querySelector(`#Quantity-${line}`) || this.querySelector(`#Drawer-quantity-${line}`);
      if (pendingInput) pendingInput.value = pendingInput.getAttribute('value');
      drawerEl.refreshIfStale();
      return;
    }
    if (window.HMLpBonus?.active || (drawerEl && (drawerEl.reading || drawerEl.retrying || drawerEl.headerAddActive))) {
      // Keyboard changes can arrive while pointer controls are locked. Keep
      // the last rendered quantity visible instead of implying this write ran.
      const pendingInput = this.querySelector(`#Quantity-${line}`) || this.querySelector(`#Drawer-quantity-${line}`);
      if (pendingInput) pendingInput.value = pendingInput.getAttribute('value');
      return false;
    }
    if (drawerEl && (drawerEl.needsSync || drawerEl.unconfirmed) && typeof drawerEl.syncThenChange === 'function') {
      const pendingInput = this.querySelector(`#Quantity-${line}`) || this.querySelector(`#Drawer-quantity-${line}`);
      return drawerEl.syncThenChange({ lineKey: pendingInput?.dataset.quantityLineKey || null,
        identity: pendingInput ? window.HMLpBonusUI?.inputIdentity(pendingInput) : null, quantity, name });
    }
    const bonus = window.HMLpBonus;
    const bonusUI = window.HMLpBonusUI;
    const bonusToken = bonus?.relevant ? bonus.begin() : null;
    if (bonusToken && bonusUI) bonusUI.render();
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);

    this.enableLoading(line);

    const action = quantity === 0 ? 'remove' : 'update';
    const quantityInput = this.querySelector(`#Quantity-${line}`) || this.querySelector(`#Drawer-quantity-${line}`);
    const lineVariantId = variantId || quantityInput?.dataset.quantityVariantId;
    const lineKey = quantityInput?.dataset.quantityLineKey;
    const lineIdentity = bonusUI && quantityInput ? bonusUI.inputIdentity(quantityInput) : null;
    const linesUpdateDeferred = this.createCartLinesUpdateEvent(action, lineVariantId, quantity, lineKey);

    // Cache sections before the fetch so we read dataset.id while elements still exist in the DOM
    const sectionsToRender = this.getSectionsToRender();

    // Cart API keys identify a line only until discounts/properties change it. A cross-tab signal refreshes this DOM first;
    // a 400 stale-key answer below refreshes it without replaying a paid write.
    const body = JSON.stringify({
      ...(lineKey ? { id: lineKey } : { line }),
      quantity,
      sections: sectionsToRender.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    // a change that does not answer within 12 s is unconfirmed (aborted, never re-sent) — review R4
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const abortTimer = controller ? window.setTimeout(() => controller.abort(), 12000) : null;
    let staleLine = false;
    let changeConfirmed = false;

    const operation = fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body }, ...(controller ? { signal: controller.signal } : {}) })
      .then((response) => {
        return response.text().then((text) => ({ status: response.status, text }));
      })
      .then(async ({ status, text }) => {
        // A rejected change must refresh stale line keys before bonus settlement.
        // Shopify can return the 400 reason in description/message, not only errors.
        // Read the actual cart; never replay this paid write automatically.
        if (status === 400) {
          bonusUI?.cancelCheckout();
          staleLine = true;
          if (linesUpdateDeferred) CartItems.fetchCartData().then((cart) => {
            if (cart) this.resolveCartLinesUpdate(linesUpdateDeferred, cart);
            else linesUpdateDeferred.resolve({ cart: null });
          });
          return;
        }
        let parsedState;
        try {
          parsedState = JSON.parse(text);
        } catch (e) {
          // Noom (T02, 13.09): 429 + Cloudflare HTML, 5xx or an empty body — the change is unconfirmed; never retried here
          throw Object.assign(new Error(`cart/change answered ${status} without JSON`), { unconfirmed: true, status });
        }

        if (status >= 400) {
          if (status !== 422) throw Object.assign(new Error(`cart/change answered ${status}`), { unconfirmed: true, status });
          parsedState.errors = parsedState.errors || parsedState.description || parsedState.message || window.cartStrings.error;
        }

        if (!parsedState.errors && bonusToken && bonusUI) {
          parsedState = await bonusUI.settle(parsedState, sectionsToRender.map(s => s.section));
        }
        if (parsedState.errors) {
          bonusUI?.cancelCheckout();
          this.dispatchCartErrorEvent(parsedState.errors, 'INVALID');
          linesUpdateDeferred?.reject(new Error(parsedState.errors));
        } else {
          this.resolveCartLinesUpdate(linesUpdateDeferred, parsedState);
          // Storage events are delivered to *other* tabs, never this one. They mark
          // their old Liquid line keys dirty without adding a read to every click.
          try { localStorage.setItem('noom-cart-revision', `${Date.now()}:${Math.random()}`); } catch (_) { /* private mode */ }
        }

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          sectionsToRender.forEach((section) => {
            const container = document.getElementById(section.id);
            if (!container || typeof parsedState.sections?.[section.section] !== 'string') return;
            // Noom R2 (14.09): on the old Elixir layout the header keeps its own cart icon — the drawer is rendered with
            // data-bubble-mode="count", so only the count bubble inside #cart-icon-bubble is synced (review P2: a change or
            // removal replaced the whole icon with the HM one)
            const drawer = section.id === 'cart-icon-bubble' ? document.querySelector('cart-drawer') : null;
            if (drawer && drawer.dataset.bubbleMode === 'count' && typeof drawer.syncCountBubble === 'function') {
              drawer.syncCountBubble(container, parsedState.sections[section.section]);
              return;
            }
            const elementToReplace = container.querySelector(section.selector) || container;
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const newIndex = lineIdentity ? parsedState.items.findIndex(item => bonusUI.itemIdentity(item) === lineIdentity) : line - 1;
          const updatedValue = parsedState.items[newIndex] ? parsedState.items[newIndex].quantity : undefined;
          let message = '';
          if ((lineIdentity ? quantity > 0 : items.length === parsedState.items.length) && updatedValue !== quantity) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const currentLine = newIndex >= 0 ? newIndex + 1 : line;
          const lineItem =
            document.getElementById(`CartItem-${currentLine}`) || document.getElementById(`CartDrawer-Item-${currentLine}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper?.querySelector('.drawer__inner-empty')) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        changeConfirmed = !parsedState.errors && !bonusUI?.needsRecovery();
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch((e) => {
        if (bonusToken && bonusUI) bonusUI.markDirty();
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        // Noom (T02, 13.09 / v4): the change is NOT confirmed — the field shows the value from before the click (not the
        // store's truth), the lines are locked until a read of the real cart (drawer card "Refresh cart"); on the cart
        // page Dawn's error line does the talking
        const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
        const drawer = this.tagName === 'CART-DRAWER-ITEMS' ? document.querySelector('cart-drawer') : null;
        if (drawer && typeof drawer.setNeedsSync === 'function') drawer.setNeedsSync(true);
        // Noom R1 (14.09): an unconfirmed change made on the cart page leaves the drawer on the same page uncertain too —
        // it shows its "refresh the cart" card before any further change instead of stale lines
        const otherDrawer = drawer ? null : document.querySelector('cart-drawer');
        if (otherDrawer && typeof otherDrawer.setNeedsSync === 'function') otherDrawer.setNeedsSync(true);
        const timedOut = e && e.name === 'AbortError';
        const kind = e && e.status === 429 ? 'ratelimit' : timedOut || (e && e.unconfirmed) ? 'change' : 'network';
        if (e && typeof e === 'object') e.hmDeferred = 'cart-lines-update';
        const message = (drawer && typeof drawer.errorText === 'function' && drawer.errorText(kind)) || window.cartStrings.error;
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = drawer ? '' : message;
        if (drawer && typeof drawer.showError === 'function') drawer.showError(kind);
        this.updateLiveRegions(line, drawer ? '' : message);
        if (quantityElement && drawer) quantityElement.focus();
        this.dispatchCartErrorEvent(message, 'SERVICE_UNAVAILABLE');
        if (linesUpdateDeferred && linesUpdateDeferred.promise && typeof linesUpdateDeferred.promise.catch === 'function') linesUpdateDeferred.promise.catch(() => {});
        linesUpdateDeferred?.reject(e);
      })
      .finally(() => {
        CartItems.activeUpdate = null;
        if (bonusToken) bonus.end(bonusToken);
        if (bonusUI) bonusUI.render();
        if (abortTimer) window.clearTimeout(abortTimer);
        this.disableLoading(line);
        if (staleLine) {
          const drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.markCrossTabDirty();
          else this.onCartUpdate();
        }
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      })
      .then(() => changeConfirmed);
    CartItems.activeUpdate = operation;
    return operation;
  }

  createCartLinesUpdateEvent(action, variantId, quantity, lineKey) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent || !variantId) return null;
    // No AJAX line key on the row — likely cached HTML rendered before this
    // attribute landed. Skip dispatch rather than emit an event with id: ''.
    if (!lineKey) return null;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines: [{ id: lineKey, quantity }],
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  resolveCartLinesUpdate(deferred, parsedState) {
    if (!deferred) return;
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent) return;

    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedState) });
  }

  dispatchCartErrorEvent(message, code) {
    const { CartErrorEvent } = window.StandardEvents || {};
    if (!CartErrorEvent) return;
    this.dispatchEvent(new CartErrorEvent({ error: message, code }));
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

// Ordinary carts have no bonus verification to hold a checkout click. Flush
// the short quantity debounce, then submit once after its confirmed response.
let waitingForCartChange = false;
function checkoutAfterCartChange(event, form, button, href) {
  if (!CartItems.pendingChanges.size && !CartItems.activeUpdate && !CartItems.draining) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (waitingForCartChange) return;
  waitingForCartChange = true;
  const formId = form?.id;
  const buttonId = button?.id;
  Promise.resolve(CartItems.flushPendingChanges()).then(confirmed => {
    if (!confirmed) return;
    if (href) { window.location.assign(href); return; }
    const currentForm = formId ? document.getElementById(formId) : form;
    const currentButton = buttonId ? document.getElementById(buttonId) : currentForm?.querySelector('[name="checkout"]');
    if (currentForm?.isConnected && (currentButton || /\/checkout\/?$/.test(new URL(currentForm.action, location.href).pathname))) {
      if (currentButton) currentForm.requestSubmit(currentButton);
      else currentForm.requestSubmit();
    }
  }).finally(() => { waitingForCartChange = false; });
}
document.addEventListener('submit', event => {
  if (event.defaultPrevented) return;
  const checkoutForm = /\/checkout\/?$/.test(new URL(event.target.action || '', location.href).pathname);
  if (event.submitter?.name !== 'checkout' && !checkoutForm) return;
  checkoutAfterCartChange(event, event.target, event.submitter);
}, true);
document.addEventListener('click', event => {
  if (event.defaultPrevented) return;
  const link = event.target.closest?.('a[href]');
  if (link && /\/checkout\/?$/.test(new URL(link.href, location.href).pathname)) {
    checkoutAfterCartChange(event, null, null, link.href);
  }
}, true);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const newNote = event.target.value;
            const noteDeferred = this.dispatchNoteUpdateEvent(newNote);

            const body = JSON.stringify({ note: newNote });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then((r) => r.json())
              .then((cart) => {
                if (!cart || cart.errors) {
                  throw Object.assign(new Error(cart?.errors), { code: 'INVALID' });
                }

                if (noteDeferred) {
                  const { CartNoteUpdateEvent } = window.StandardEvents || {};
                  if (CartNoteUpdateEvent) {
                    noteDeferred.resolve({ cart: CartNoteUpdateEvent.createCartFromAjaxResponse(cart) });
                  }
                }
                CartPerformance.measureFromEvent('note-update:user-action', event);
              })
              .catch((e) => {
                noteDeferred?.reject(e);
                const { CartErrorEvent } = window.StandardEvents || {};
                if (CartErrorEvent) {
                  this.dispatchEvent(
                    new CartErrorEvent({
                      error: e.message || 'Note update failed',
                      code: e.code || 'SERVICE_UNAVAILABLE',
                    })
                  );
                }
              });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }

      dispatchNoteUpdateEvent(newNote) {
        const { CartNoteUpdateEvent } = window.StandardEvents || {};
        if (!CartNoteUpdateEvent) return null;

        const context = this.closest('dialog') || this.closest('cart-drawer') ? 'dialog' : 'cart';
        const deferred = CartNoteUpdateEvent.createPromise();

        this.dispatchEvent(
          new CartNoteUpdateEvent({
            context,
            note: newNote,
            promise: deferred.promise,
          })
        );

        return deferred;
      }
    }
  );
}
